import { useState, useRef, useEffect, useCallback } from "react";

const DEFAULT_BATCH_SIZE = 50;

/**
 * Unified scroll-driven hook for two complementary features:
 *
 *  1. **Progressive rendering** (`enabled = true`) – renders in-memory data in
 *     batches so the browser doesn't mount thousands of DOM nodes at once.
 *  2. **Infinite scroll fetching** (`onLoadMore` + `hasMoreData`) – calls back
 *     to the parent to fetch the next page from the server.
 *
 * Both use a single IntersectionObserver on a sentinel element. When the
 * sentinel scrolls into view the hook first tries to render more cached rows;
 * once they're exhausted it delegates to `onLoadMore`.
 *
 * @param {number} totalCount  – number of items currently in the dataset
 * @param {object} [options]
 * @param {boolean}  [options.enabled=true]        – toggle progressive rendering
 * @param {number}   [options.batchSize=50]        – rows added per scroll batch
 * @param {string}   [options.rootMargin="200px"]  – IntersectionObserver margin
 * @param {React.RefObject<Element>} [options.rootRef] – the scroll container to observe within
 *                                                  (a ref, read when the sentinel mounts); defaults
 *                                                  to the browser viewport
 * @param {Function} [options.onLoadMore]          – callback to fetch the next page
 * @param {boolean}  [options.hasMoreData=false]    – whether more pages exist
 * @returns {{ visibleCount: number, sentinelRef: React.RefCallback, hasMore: boolean }}
 */
const useProgressiveRender = (
  totalCount,
  {
    enabled = true,
    batchSize = DEFAULT_BATCH_SIZE,
    rootMargin = "200px",
    rootRef,
    onLoadMore,
    hasMoreData = false,
  } = {},
) => {
  const [visibleCount, setVisibleCount] = useState(
    enabled ? Math.min(batchSize, totalCount) : totalCount,
  );

  // Stable refs so the observer callback always sees the latest values
  // without needing them in the useCallback deps (which would cause
  // unnecessary observer reconnections).
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const visibleCountRef = useRef(visibleCount);
  visibleCountRef.current = visibleCount;

  // Reset visible count when the dataset changes or the feature is toggled.
  // When totalCount grows (data appended via infinite scroll) we keep the
  // current position so the view doesn't jump back to the first batch.
  useEffect(() => {
    setVisibleCount((prev) => {
      if (!enabled) return totalCount;
      if (totalCount > prev) return prev;
      return Math.min(batchSize, totalCount);
    });
  }, [totalCount, enabled, batchSize]);

  // The sentinel's callback ref only records the node; the observer itself is created in an
  // effect, which runs after every ref in the commit has been attached — so a scroll-container
  // `rootRef` passed by the parent is already populated even when the sentinel mounts in the same
  // commit as the container.
  const [sentinel, setSentinel] = useState(null);
  const sentinelRef = useCallback((node) => setSentinel(node), []);

  useEffect(() => {
    if (!sentinel || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        if (enabled && visibleCountRef.current < totalCount) {
          setVisibleCount((prev) => Math.min(prev + batchSize, totalCount));
        } else {
          onLoadMoreRef.current?.();
        }
      },
      { root: rootRef?.current ?? null, rootMargin },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, enabled, totalCount, batchSize, rootMargin, rootRef]);

  const clampedCount = enabled
    ? Math.min(visibleCount, totalCount)
    : totalCount;

  return {
    visibleCount: clampedCount,
    sentinelRef,
    hasMore: (enabled && clampedCount < totalCount) || hasMoreData,
  };
};

export default useProgressiveRender;
